import { User, UserManager } from 'oidc-client-ts';

import config from 'config/site.config';

const { oidc } = config;

const baseUrl = `${window.location.protocol}//${window.location.hostname}${
  window.location.port ? `:${window.location.port}` : ''
}`;

const userManagerConfig = {
  authority: oidc.authority,
  client_id: 'ditio_web_client',
  response_type: 'code',
  scope:
    'openid masstransportapiv1 reportingapiv1 ditioapiv3 profile offline_access',
  redirect_uri: `${baseUrl}/callback`,
  // Distinct endpoint for silent-renew iframe so it doesn't reuse /callback.
  // /callback navigates window.location on success — inside an iframe that loads
  // the whole SPA in the iframe (≈5s hang) and clobbers the redirect stash.
  // /silent hosts silent/index.html → processSilentRenew(), which just posts the
  // new token up to the parent via postMessage; no navigation, no state touch.
  silent_redirect_uri: `${baseUrl}/silent`,
  post_logout_redirect_uri: `${baseUrl}/logged-out`,
  automaticSilentRenew: true,
  monitorSession: false,
  accessTokenExpiringNotificationTimeInSeconds: 60,
};

let instance = null;

// Cross-tab broadcast so only one tab performs a refresh-token rotation;
// siblings pick up the rotated token via postMessage instead of racing the
// OneTimeOnly rotation on the server.
const broadcastChannel =
  typeof BroadcastChannel === 'function'
    ? new BroadcastChannel('oidc-refresh')
    : null;

export const getUserManager = () => {
  if (instance) return instance;

  instance = new UserManager(userManagerConfig);

  instance.events.addUserLoaded((user) => {
    if (
      broadcastChannel &&
      user &&
      typeof user.toStorageString === 'function'
    ) {
      broadcastChannel.postMessage({
        type: 'user-loaded',
        storage: user.toStorageString(),
      });
    }
  });

  if (broadcastChannel) {
    broadcastChannel.addEventListener('message', (event) => {
      if (
        !event.data ||
        event.data.type !== 'user-loaded' ||
        !event.data.storage
      ) {
        return;
      }
      try {
        const user = User.fromStorageString(event.data.storage);
        instance.storeUser(user);
      } catch {
        // Malformed message — ignore; the owning tab will retry on next rotation.
      }
    });
  }

  return instance;
};

// Deduplicate concurrent silent-renew attempts so only one refresh-token
// exchange is in flight at a time. Without this, overlapping callers race
// on the rotating refresh token: one wins and the loser comes back as
// `invalid_grant`, which the app treats as a hard logout. The Web Locks
// API extends the dedup across tabs in the same origin.
let inFlightSilentSignin = null;

const runSilentSignin = () =>
  getUserManager()
    .signinSilent()
    .then((user) => {
      if (!user || !user.access_token) {
        throw new Error('Silent renew did not return an access token');
      }
      return user;
    });

export const signinSilentShared = () => {
  if (inFlightSilentSignin) return inFlightSilentSignin;

  const hasWebLocks =
    typeof navigator !== 'undefined' &&
    navigator.locks &&
    typeof navigator.locks.request === 'function';

  inFlightSilentSignin = (
    hasWebLocks
      ? navigator.locks.request('oidc-refresh', async () => {
          // A sibling tab may have rotated the token while we waited for the
          // lock. If the stored token is still valid, skip the network call.
          const existing = await getUserManager().getUser();
          if (existing && existing.access_token && !existing.expired) {
            return existing;
          }
          return runSilentSignin();
        })
      : runSilentSignin()
  ).finally(() => {
    inFlightSilentSignin = null;
  });

  return inFlightSilentSignin;
};
